import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@mlm/ui';
import { getAccountingHealth, getAccountingOverview } from '../../lib/api';
import { Money, PageHeader, SectionLabel } from './_shared';

/**
 * Accounting overview — LIVE (Phase 4). A handful of aggregates straight off
 * the GL + funds register, with links into the pages that explain each number.
 */

const peso = (v: string) =>
  `₱${Number(v).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function AccountingOverviewPage() {
  const overviewQ = useQuery({ queryKey: ['accounting-overview'], queryFn: getAccountingOverview });
  const o = overviewQ.data;
  // The health scan reads the whole journal, so it runs when asked, not on
  // every page view.
  const [healthRequested, setHealthRequested] = useState(false);
  const healthQ = useQuery({
    queryKey: ['accounting-health'],
    queryFn: getAccountingHealth,
    enabled: healthRequested,
    staleTime: 0,
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Accounting"
        subtitle="Live books: every money movement the platform records, posted double-entry by the GL poster."
        actions={
          o ? (
            <Badge variant={o.tbInBalance ? 'success' : 'destructive'}>
              {o.tbInBalance ? 'Books in balance' : 'OUT OF BALANCE — investigate'}
            </Badge>
          ) : undefined
        }
      />

      {overviewQ.isLoading || !o ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Card>
              <CardContent className="pt-4">
                <SectionLabel>Cash &amp; bank (register)</SectionLabel>
                <p className="text-xl font-semibold">
                  <Money>{peso(o.cashTotal)}</Money>
                </p>
                <Link className="text-xs text-muted-foreground underline" to="/accounting/funds">
                  Cash &amp; Bank
                </Link>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <SectionLabel>Net sales · this month</SectionLabel>
                <p className="text-xl font-semibold">
                  <Money>{peso(o.mtd.netSales)}</Money>
                </p>
                <Link
                  className="text-xs text-muted-foreground underline"
                  to="/accounting/statements"
                >
                  Income Statement
                </Link>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <SectionLabel>Net income · this month</SectionLabel>
                <p className="text-xl font-semibold">
                  <Money>{peso(o.mtd.netIncome)}</Money>
                </p>
                <Link
                  className="text-xs text-muted-foreground underline"
                  to="/accounting/statements"
                >
                  Statements
                </Link>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <SectionLabel>Needs attention</SectionLabel>
                <p className="text-xl font-semibold">{o.needsAttention.toLocaleString()}</p>
                <Link
                  className="text-xs text-muted-foreground underline"
                  to="/accounting/journal?unmapped=1"
                >
                  Journal · needs attention
                </Link>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Posting status</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-x-10 gap-y-2 text-sm">
              <div>
                <SectionLabel>Journal entries posted</SectionLabel>
                <p>{o.entriesPosted.toLocaleString()}</p>
              </div>
              <div>
                <SectionLabel>Latest posting date</SectionLabel>
                <p>{o.lastPostingDate ?? '—'}</p>
              </div>
              {o.posterFailures > 0 && (
                <div>
                  <SectionLabel>Stuck, unposted</SectionLabel>
                  <p className="flex items-center gap-2">
                    {o.posterFailures.toLocaleString()}
                    <Badge variant="destructive">Needs a look</Badge>
                  </p>
                </div>
              )}
              {o.uncosted.batches + o.uncosted.consignmentBillings + o.uncosted.completions > 0 && (
                <div>
                  <SectionLabel>Waiting on a cost</SectionLabel>
                  <p
                    className="flex items-center gap-2"
                    title="Stock receipts, consignment billings and completed sales whose products have no cost yet. Their inventory and COGS stay unrelieved until you set the cost — the next run then books them automatically."
                  >
                    {(
                      o.uncosted.batches +
                      o.uncosted.consignmentBillings +
                      o.uncosted.completions
                    ).toLocaleString()}
                    <Badge variant="secondary">Set a cost</Badge>
                  </p>
                </div>
              )}
              <div>
                <SectionLabel>Posting start</SectionLabel>
                <p className="flex items-center gap-2">
                  {o.postingStart.date ?? (o.postingStart.pinned ? 'All history' : '—')}
                  {o.postingStart.pinned && <Badge variant="secondary">Pinned</Badge>}
                </p>
              </div>
              <p className="max-w-xl text-xs text-muted-foreground">
                {o.postingStart.pinned && (
                  <>
                    The posting start pinned on the first tick and is what the poster actually uses
                    — the Settings field cannot change it any more.{' '}
                  </>
                )}
                The poster sweeps every ~30 seconds. Lines flagged “needs attention” booked honestly
                but imprecisely — an unmapped wallet kind (6900), a document with no Cash &amp; Bank
                register account, or goods with no recorded cost — and are worth a look before
                month-end reporting.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
              <CardTitle>Books health check</CardTitle>
              <div className="flex items-center gap-2">
                {healthQ.data && (
                  <Badge variant={healthQ.data.ok ? 'success' : 'destructive'}>
                    {healthQ.data.ok
                      ? 'All checks pass'
                      : `${healthQ.data.checks.filter((c) => !c.ok).length} failing`}
                  </Badge>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  disabled={healthQ.isFetching}
                  onClick={() => {
                    setHealthRequested(true);
                    void healthQ.refetch();
                  }}
                >
                  {healthQ.isFetching ? 'Checking…' : 'Run checks'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {!healthQ.data ? (
                <p className="text-xs text-muted-foreground">
                  Re-derives every GL figure from the subledger that fed it — the register, the
                  wallets, the payout requests, the supplier documents. Run it before enabling
                  accounting for a client, and after any deploy that touches posting.
                </p>
              ) : (
                <ul className="space-y-1">
                  {healthQ.data.checks.map((c) => (
                    <li key={c.id} className="flex items-start gap-2">
                      <Badge variant={c.ok ? 'success' : 'destructive'}>
                        {c.ok ? 'OK' : 'FAIL'}
                      </Badge>
                      <span>
                        {c.label}
                        <span className="block text-xs text-muted-foreground">{c.detail}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
